import type { ActivityHistoryAction } from '@store/reducers/manager/activityHistory/types'
import { getId } from '@store/utils'

import { actionTypesName } from '@/constants/actionTypesName'
import { MONTHS } from '@/constants/constants'
import type {
  ActivityHistoriesGroupedByUserAndDate,
  GroupedActivityHistories,
} from '@/types'

type DateFormat = 'MM-DD-YYYY'

export const getProjectCreateDate = (datetime: string, format: DateFormat) => {
  const today = new Date()

  const date = new Date(datetime)
  const day = date.getUTCDate()
  const month = date.getUTCMonth()
  const year = date.getUTCFullYear()

  if (
    day === today.getUTCDate() &&
    month === today.getUTCMonth() &&
    year === today.getUTCFullYear()
  ) {
    return 'Today'
  }

  if (date.getUTCDate() === today.getUTCDate() - 1) {
    return 'Yesterday'
  }

  if (format === 'MM-DD-YYYY') {
    return `${MONTHS[month]} ${day}, ${year}`
  }

  return `${day} ${MONTHS[month]}, ${year}`
}

export const formatTime = (date: string) => {
  const inputDate = new Date(date)
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: userTimeZone,
  })

  return formatter.format(inputDate)
}

function hoursDiff(date1: string, date2: string) {
  const diff = Math.abs(+new Date(date1) - +new Date(date2))
  return diff / 36e5 // 1 час = 36e5 миллисекунд
}

const groupByUserAndDate = (actions: ActivityHistoryAction[]) => {
  const result: ActivityHistoriesGroupedByUserAndDate[] = []

  actions.forEach((item: any) => {
    const lastItem = result[result.length - 1]

    if (
      lastItem &&
      lastItem.authorUuid === item.authorUuid &&
      hoursDiff(
        lastItem.actions[lastItem.actions.length - 1].createdAt,
        item.createdAt
      ) < 1
    ) {
      result[result.length - 1].actions.push(item)
    } else {
      result.push({
        authorUuid: item.authorUuid,
        id: getId(),
        actions: [item],
      })
    }
  })

  return result
}

export const groupedActions = (actions: ActivityHistoryAction[]) => {
  const groupedByDate = actions.reduce((result: any, item) => {
    const date = new Date(item.createdAt).toISOString().split('T')[0]
    if (!result[date]) {
      result[date] = []
    }
    result[date].push(item)
    return result
  }, {})

  const groupedData = Object.keys(groupedByDate).reduce(
    (acc: GroupedActivityHistories[], date: string) => {
      const items = groupedByDate[date]

      return [
        ...acc,
        {
          date,
          id: date,
          actions: items,
          totalChanges: items.length,
        },
      ]
    },
    []
  )

  return groupedData.reduce((acc: any, item: GroupedActivityHistories) => {
    return [
      ...acc,
      {
        ...item,
        groupedByUserAndDate: groupByUserAndDate(item.actions),
      },
    ]
  }, [])
}

export const getActionTypeName = (actionType: string): string => {
  return (
    actionTypesName[actionType as keyof typeof actionTypesName] ||
    'Project updated'
  )
}
